/**
 * RuleEditor — edit one rule as a sentence with blanks.
 *
 * The preview at the top is the rule's sentence; each value is a tappable blank
 * that jumps to its field below. Controls are chosen from the value's type in
 * the catalogue (steppers, chips, yes/no), so nothing needs typing except names.
 */
import { useMemo, useState } from 'react';
import RuleSheet from './RuleSheet';
import { render, sentence, missingRequired, KIND_META } from './ruleText';
import StandingsExample from './StandingsExample';

const chip = (on) => `px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
  on ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'bg-white text-[#2B3A67] border-gray-200 hover:border-[#2B3A67]/40'}`;

function Stepper({ value, onChange, spec, allowEmpty }) {
  const step = spec.type === 'number' && spec.max && spec.max <= 10 ? 0.5 : 1;
  const clamp = (v) => Math.min(spec.max ?? 999, Math.max(spec.min ?? 0, v));
  const n = typeof value === 'number' ? value : null;
  return (
    <div className="flex items-center gap-2">
      <button type="button" className="w-11 h-11 rounded-xl bg-[#E9EBF1] text-[#2B3A67] text-xl font-bold"
        onClick={() => onChange(clamp((n ?? (spec.min ?? 0) + step) - step))} aria-label="Less">−</button>
      <input inputMode="decimal" className="w-20 h-11 text-center rounded-xl border border-gray-200 text-lg font-bold text-[#2B3A67]"
        value={n ?? ''} placeholder="–"
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') return onChange(null);
          const v = Number(raw);
          if (!Number.isNaN(v)) onChange(v);
        }} />
      <button type="button" className="w-11 h-11 rounded-xl bg-[#E9EBF1] text-[#2B3A67] text-xl font-bold"
        onClick={() => onChange(clamp((n ?? (spec.min ?? 0) - step) + step))} aria-label="More">+</button>
      {allowEmpty && n !== null && (
        <button type="button" className="text-xs text-gray-500 underline ml-1" onClick={() => onChange(null)}>Clear</button>
      )}
    </div>
  );
}

function ValueField({ name, spec, value, onChange }) {
  const optional = !spec.required;
  switch (spec.type) {
    case 'int':
    case 'number':
      return <Stepper value={value} onChange={onChange} spec={spec} allowEmpty={optional} />;
    case 'int_or':
    case 'number_or':
      return (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(spec.special || {}).map(([k, label]) => (
            <button key={k} type="button" className={chip(value === k)} onClick={() => onChange(value === k ? null : k)}>{label}</button>
          ))}
          <Stepper value={typeof value === 'number' ? value : null} onChange={onChange} spec={spec} allowEmpty={optional} />
        </div>
      );
    case 'bool': {
      const labels = spec.labels || { true: 'Yes', false: 'No' };
      const opts = [[true, labels.true], [false, labels.false]];
      return (
        <div className="flex flex-wrap gap-2">
          {opts.map(([v, label]) => (
            <button key={String(v)} type="button" className={chip(value === v)} onClick={() => onChange(value === v && optional ? null : v)}>
              {label.charAt(0).toUpperCase() + label.slice(1)}
            </button>
          ))}
        </div>
      );
    }
    case 'enum':
      return (
        <div className="flex flex-wrap gap-2">
          {Object.entries(spec.options).map(([k, label]) => (
            <button key={k} type="button" className={chip(value === k)} onClick={() => onChange(value === k && optional ? null : k)}>{label}</button>
          ))}
        </div>
      );
    case 'enum_list':
    case 'ordered_enum_list': {
      const list = value || [];
      const ordered = spec.type === 'ordered_enum_list';
      return (
        <div className="flex flex-wrap gap-2">
          {Object.entries(spec.options).map(([k, label]) => {
            const idx = list.indexOf(k);
            return (
              <button key={k} type="button" className={chip(idx >= 0)}
                onClick={() => onChange(idx >= 0 ? list.filter((x) => x !== k) : [...list, k])}>
                {ordered && idx >= 0 && <span className="mr-1 text-[#F4D341]">{idx + 1}.</span>}{label}
              </button>
            );
          })}
          {ordered && <p className="w-full text-xs text-gray-500">Tap in the order they apply.</p>}
        </div>
      );
    }
    case 'over_list': {
      const list = value || [];
      const toggle = (v) => onChange(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
      return (
        <div className="flex flex-wrap gap-2">
          {Object.entries(spec.special || {}).map(([k, label]) => (
            <button key={k} type="button" className={chip(list.includes(k))} onClick={() => toggle(k)}>{label}</button>
          ))}
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <button key={n} type="button" className={chip(list.includes(n))} onClick={() => toggle(n)}>Over {n}</button>
          ))}
        </div>
      );
    }
    case 'count_map': {
      if (spec.keys) {   // fixed categories (A to D): a count for each, 0 = not needed
        const cur = value || {};
        const set = (k, n) => {
          const next = { ...cur };
          if (n > 0) next[k] = n; else delete next[k];
          onChange(Object.keys(next).length ? next : null);
        };
        return (
          <div className="grid grid-cols-2 gap-2">
            {spec.keys.map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-8 text-sm font-bold text-[#2B3A67]">{k}</span>
                <button type="button" className="w-9 h-9 rounded-xl bg-[#E9EBF1] text-[#2B3A67] font-bold" aria-label={`Fewer ${k}`}
                  onClick={() => set(k, Math.max(0, (cur[k] || 0) - 1))}>−</button>
                <span className="w-6 text-center font-bold">{cur[k] || 0}</span>
                <button type="button" className="w-9 h-9 rounded-xl bg-[#E9EBF1] text-[#2B3A67] font-bold" aria-label={`More ${k}`}
                  onClick={() => set(k, Math.min(40, (cur[k] || 0) + 1))}>+</button>
              </div>
            ))}
            <p className="col-span-2 text-xs text-gray-500">Categories are A to D, as players registered.</p>
          </div>
        );
      }
      const rows = Object.entries(value || {});
      const set = (next) => onChange(Object.fromEntries(next));
      return (
        <div className="space-y-2">
          {rows.map(([k, n], i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="flex-1 h-11 px-3 rounded-xl border border-gray-200 text-sm" value={k} placeholder="Category name"
                onChange={(e) => set(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
              <input inputMode="numeric" className="w-16 h-11 text-center rounded-xl border border-gray-200 font-bold" value={n}
                onChange={(e) => set(rows.map((r, j) => (j === i ? [r[0], Number(e.target.value) || 0] : r)))} />
              <button type="button" className="w-9 h-11 text-gray-400 text-xl" aria-label="Remove"
                onClick={() => set(rows.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <button type="button" className="text-sm font-semibold text-[#1F7A75]"
            onClick={() => set([...rows, [`Category ${rows.length + 1}`, 1]])}>+ Add a category</button>
        </div>
      );
    }
    case 'text':
      return (
        <input className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm" value={value || ''} maxLength={spec.max || 80}
          placeholder={spec.label} onChange={(e) => onChange(e.target.value || null)} />
      );
    default:
      return null;
  }
}

const STAGE_KEYS = ['league', 'knockout', 'final'];

export default function RuleEditor({ open, catalogue, rule, onClose, onSave, onDelete, saving }) {
  const [draft, setDraft] = useState(() => ({ params: {}, stages: null, source_text: '', ...rule }));
  const [showOwnWords, setShowOwnWords] = useState(!!rule?.source_text && rule?.block !== 'text');
  const block = catalogue?.blocks?.[draft.block];
  const kind = KIND_META[block?.kind || 'text'];
  const segs = useMemo(() => (block ? render(block, draft) : []), [block, draft]);
  const missing = missingRequired(block, draft.params);
  const isText = draft.block === 'text';

  if (!open || !block) return null;
  const setParam = (name, v) => setDraft((d) => ({ ...d, params: { ...d.params, [name]: v } }));
  const stages = draft.stages || [];
  const toggleStage = (s) => {
    const next = stages.includes(s) ? stages.filter((x) => x !== s) : [...stages, s];
    setDraft((d) => ({ ...d, stages: next.length === 0 || next.length === 3 ? null : next }));
  };
  const jumpTo = (name) => document.getElementById(`rule-field-${name}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const clean = () => ({
    block: draft.block,
    params: Object.fromEntries(Object.entries(draft.params || {}).filter(([, v]) => v !== null && v !== undefined && v !== '')),
    stages: draft.stages,
    source_text: (draft.source_text || '').trim() || null,
    section: draft.section,
  });

  return (
    <RuleSheet open={open} onClose={onClose} title={block.label} testId="rule-editor"
      subtitle={<span className="inline-flex items-center gap-1" style={{ color: kind.color }}><i className={`ph-bold ${kind.icon}`} /> {kind.label}</span>}
      footer={(
        <div className="flex gap-2">
          {onDelete && (
            <button type="button" onClick={onDelete} disabled={saving}
              className="h-11 px-4 rounded-xl border border-[#E54562] text-[#B3263F] font-semibold text-sm" data-testid="rule-delete">
              Delete
            </button>
          )}
          <button type="button" disabled={saving || (isText && !(draft.source_text || '').trim())} onClick={() => onSave(clean())}
            className="flex-1 h-11 rounded-xl bg-[#2B3A67] text-white font-bold text-sm disabled:opacity-50" data-testid="rule-save">
            {saving ? 'Saving…' : missing.length ? 'Save for now' : 'Save rule'}
          </button>
        </div>
      )}>
      {isText ? (
        <textarea className="w-full min-h-[140px] p-3 rounded-xl border border-[#3AAFA9] text-sm leading-relaxed"
          placeholder="Write the rule in your own words" value={draft.source_text || ''} maxLength={2000}
          onChange={(e) => setDraft((d) => ({ ...d, source_text: e.target.value }))} data-testid="rule-text" />
      ) : (
        <>
          <div className="rounded-xl bg-[#F5F7FA] p-3 text-[15px] leading-relaxed text-[#2B3A67]" data-testid="rule-preview">
            {segs.map((s, i) => {
              if (!('param' in s)) return s.show ? <span key={i}>{s.text}</span> : null;
              const spec = block.params[s.param];
              if (!s.show && !(s.missing && s.group !== null && spec?.type !== 'bool')) return null;
              return (
                <button key={i} type="button" onClick={() => jumpTo(s.param)}
                  className={`mx-0.5 px-1.5 rounded-md font-bold ${s.missing
                    ? (spec?.required ? 'bg-[#FDF5D3] border border-dashed border-[#F4D341] text-[#2B3A67]' : 'text-gray-400 border border-dashed border-gray-300')
                    : 'bg-[#E9EBF1] text-[#2B3A67]'}`}>
                  {s.missing ? (spec?.required ? '?' : `+ ${spec?.label?.toLowerCase()}`) : s.text}
                </button>
              );
            })}
          </div>
          {missing.length > 0 && (
            <p className="mt-2 text-xs font-semibold text-[#2B3A67] bg-[#FDF5D3] rounded-lg px-2 py-1.5">
              <i className="ph-bold ph-warning" /> Fill in {missing.join(', ').toLowerCase()} before publishing.
            </p>
          )}
          <div className="mt-4 space-y-4">
            {Object.entries(block.params).map(([name, spec]) => (
              <div key={name} id={`rule-field-${name}`}>
                <p className="text-xs font-bold text-gray-500 mb-1.5">
                  {spec.label}{spec.required ? '' : <span className="font-normal"> · optional</span>}
                </p>
                <ValueField name={name} spec={spec} value={draft.params?.[name] ?? null} onChange={(v) => setParam(name, v)} />
              </div>
            ))}
          </div>
          <StandingsExample block={draft.block} params={draft.params} />
        </>
      )}

      <div className="mt-5">
        <p className="text-xs font-bold text-gray-500 mb-1.5">Applies in</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={chip(!draft.stages)} onClick={() => setDraft((d) => ({ ...d, stages: null }))}>Every match</button>
          {STAGE_KEYS.map((s) => (
            <button key={s} type="button" className={chip(stages.includes(s))} onClick={() => toggleStage(s)}>
              {catalogue.stage_labels[s]}
            </button>
          ))}
        </div>
      </div>

      {!isText && (
        <div className="mt-5">
          {showOwnWords ? (
            <>
              <p className="text-xs font-bold text-gray-500 mb-1.5">Your own words <span className="font-normal">· shown to players with the rule</span></p>
              <textarea className="w-full min-h-[70px] p-3 rounded-xl border border-gray-200 text-sm" maxLength={2000}
                value={draft.source_text || ''} onChange={(e) => setDraft((d) => ({ ...d, source_text: e.target.value }))} />
            </>
          ) : (
            <button type="button" className="text-sm font-semibold text-[#1F7A75]" onClick={() => setShowOwnWords(true)}>
              + Add your own words
            </button>
          )}
        </div>
      )}

      <div className="mt-5 text-xs text-gray-500">
        <span className="font-bold">Players will read:</span> {sentence(block, clean(), catalogue.stage_labels) || '…'}
      </div>
    </RuleSheet>
  );
}
